import { type DmkNetworkClient } from "@ledgerhq/device-management-kit";
import { Left, Right } from "purify-ts";
import { beforeEach, describe, expect, it, vi } from "vitest";

import { type ContextModuleServiceConfig } from "@/config/model/ContextModuleConfig";

import { HttpDynamicNetworkDataSource } from "./HttpDynamicNetworkDataSource";

describe("HttpNetworkDataSource", () => {
  let datasource: HttpDynamicNetworkDataSource;
  let httpMock: { get: ReturnType<typeof vi.fn> };
  const mockConfig: ContextModuleServiceConfig = {
    cal: {
      url: "https://global.api.prd.ledger.com/cal/v1",
      mode: "prod",
      branch: "main",
    },
  } as ContextModuleServiceConfig;

  const mockNetworkData = [
    {
      id: "ethereum",
      descriptors: {
        flex: {
          data: "0x010108020101510101230800000000000000895207506f6c79676f6e2403504f4c",
          descriptorType: "network",
          descriptorVersion: "v1",
          signatures: {
            prod: "3045022100c116b5470c266c2947b92aa9eadbe3da03305efc6b8fee041e04e8484a9834af022041d5b82b359614ea8dd94a542dfd87b0ae6f3b4a075aad9a53a030064ab42cd0",
            test: "3045022100f78bac5c9c3f2ceeea26680aaea17be61fce84ae7ec983a8194e68275f4fe5900220231afbe39fcec63edfea2b4e787a72d79ce53d6a4c408f24b5a116905a9082d3",
          },
        },
        stax: {
          data: "0x010108020101510101230800000000000000895207506f6c79676f6e2403504f4c53204aa5034d2fd4c46647d382aa64d0a03d06b185512bb7942390318bda18d0423a",
          descriptorType: "network",
          descriptorVersion: "v1",
          signatures: {
            prod: "3045022100cf42c039c16fc95dc97c09f15cdd93bed0e63ee45cf5c38c2b30bb8a3bc17f8d022053a96c9e51695c3c1c1a31f5cbf84bd6febadc97f4bb02bdc67cf3e24ad0c32d",
            test: "304402207e1e1b8f99b45c95b0cf1e90f46cf07e6a90f82c49ccdb6e2e95ab0c1e2f5a370220784e1fac16bb01c5d096733e1cbf067d616e4de659fda2c388f8c3502e7f7f80",
          },
        },
      },
    },
  ];

  beforeEach(() => {
    vi.clearAllMocks();
    httpMock = { get: vi.fn() };
    datasource = new HttpDynamicNetworkDataSource(
      mockConfig,
      httpMock as unknown as DmkNetworkClient,
    );
  });

  describe("getNetworkConfiguration", () => {
    it("should return network configuration successfully", async () => {
      httpMock.get.mockResolvedValue(mockNetworkData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(httpMock.get).toHaveBeenCalledWith(
        `${mockConfig.cal.url}/networks`,
        {
          params: {
            output: "id,descriptors,icons",
            chain_id: 1,
          },
        },
      );

      expect(result).toEqual(
        Right({
          id: "ethereum",
          descriptors: {
            flex: {
              data: "0x010108020101510101230800000000000000895207506f6c79676f6e2403504f4c",
              descriptorType: "network",
              descriptorVersion: "v1",
              signatures: {
                prod: "3045022100c116b5470c266c2947b92aa9eadbe3da03305efc6b8fee041e04e8484a9834af022041d5b82b359614ea8dd94a542dfd87b0ae6f3b4a075aad9a53a030064ab42cd0",
                test: "3045022100f78bac5c9c3f2ceeea26680aaea17be61fce84ae7ec983a8194e68275f4fe5900220231afbe39fcec63edfea2b4e787a72d79ce53d6a4c408f24b5a116905a9082d3",
              },
              icon: undefined,
            },
            stax: {
              data: "0x010108020101510101230800000000000000895207506f6c79676f6e2403504f4c53204aa5034d2fd4c46647d382aa64d0a03d06b185512bb7942390318bda18d0423a",
              descriptorType: "network",
              descriptorVersion: "v1",
              signatures: {
                prod: "3045022100cf42c039c16fc95dc97c09f15cdd93bed0e63ee45cf5c38c2b30bb8a3bc17f8d022053a96c9e51695c3c1c1a31f5cbf84bd6febadc97f4bb02bdc67cf3e24ad0c32d",
                test: "304402207e1e1b8f99b45c95b0cf1e90f46cf07e6a90f82c49ccdb6e2e95ab0c1e2f5a370220784e1fac16bb01c5d096733e1cbf067d616e4de659fda2c388f8c3502e7f7f80",
              },
              icon: undefined,
            },
          },
        }),
      );
    });

    it("should return error when network data is not found", async () => {
      httpMock.get.mockResolvedValue([]);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(new Error("Network configuration not found for chain ID: 1")),
      );
    });

    it("should return error when network data is undefined", async () => {
      httpMock.get.mockResolvedValue(null);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(new Error("Network configuration not found for chain ID: 1")),
      );
    });

    it("should return error when http.get throws an error", async () => {
      httpMock.get.mockRejectedValue(new Error("Network error"));

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(Left(new Error("Network error")));
    });

    it("should return generic error when http.get throws non-Error", async () => {
      httpMock.get.mockRejectedValue("String error");

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(new Error("Failed to fetch network configuration")),
      );
    });

    it("should handle invalid data - missing id", async () => {
      const invalidData = [
        {
          // Missing id
          descriptors: {},
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should handle invalid data - missing descriptors", async () => {
      const invalidData = [
        {
          id: "ethereum",
          // Missing descriptors
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should handle invalid descriptor - missing data field", async () => {
      const invalidData = [
        {
          id: "ethereum",
          descriptors: {
            flex: {
              // Missing data
              descriptorType: "network",
              descriptorVersion: "v1",
              signatures: { prod: "sig1", test: "sig2" },
            },
          },
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should handle invalid descriptor - missing signatures", async () => {
      const invalidData = [
        {
          id: "ethereum",
          descriptors: {
            flex: {
              data: "0x0101",
              descriptorType: "network",
              descriptorVersion: "v1",
              // Missing signatures
            },
          },
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should handle invalid signatures - missing prod", async () => {
      const invalidData = [
        {
          id: "ethereum",
          descriptors: {
            flex: {
              data: "0x0101",
              descriptorType: "network",
              descriptorVersion: "v1",
              signatures: {
                // Missing prod
                test: "sig2",
              },
            },
          },
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should include icons when available", async () => {
      const dataWithIcons = [
        {
          id: "ethereum",
          descriptors: {
            flex: {
              data: "0x0101",
              descriptorType: "network",
              descriptorVersion: "v1",
              signatures: {
                prod: "sig1",
                test: "sig2",
              },
            },
            stax: {
              data: "0x0102",
              descriptorType: "network",
              descriptorVersion: "v1",
              signatures: {
                prod: "sig3",
                test: "sig4",
              },
            },
          },
          icons: {
            flex: "icon1",
            stax: "icon2",
          },
        },
      ];
      httpMock.get.mockResolvedValue(dataWithIcons);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result.isRight()).toBe(true);
      const configuration = result.unsafeCoerce();
      expect(configuration.descriptors.flex.icon).toBe("icon1");
      expect(configuration.descriptors.stax.icon).toBe("icon2");
    });

    it("should transform data correctly for Polygon chain ID", async () => {
      httpMock.get.mockResolvedValue(mockNetworkData);

      const result = await datasource.getDynamicNetworkConfiguration(137);

      expect(httpMock.get).toHaveBeenCalledWith(
        `${mockConfig.cal.url}/networks`,
        {
          params: {
            output: "id,descriptors,icons",
            chain_id: 137,
          },
        },
      );

      expect(result.isRight()).toBe(true);
      const configuration = result.unsafeCoerce();
      expect(Object.keys(configuration.descriptors)).toEqual(["flex", "stax"]);
    });

    it("should handle invalid data - null data", async () => {
      const invalidData = [null];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(new Error("Network configuration not found for chain ID: 1")),
      );
    });

    it("should handle invalid data - non-object data", async () => {
      const invalidData = ["string_instead_of_object"];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should handle invalid data - icons as string instead of object", async () => {
      const invalidData = [
        {
          id: "ethereum",
          descriptors: {
            flex: {
              data: "0x0101",
              descriptorType: "network",
              descriptorVersion: "v1",
              signatures: {
                prod: "sig1",
                test: "sig2",
              },
            },
          },
          icons: "should_be_object_not_string",
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should handle invalid data - icons as number instead of object (line 93)", async () => {
      const invalidData = [
        {
          id: "ethereum",
          descriptors: {
            flex: {
              data: "0x0101",
              descriptorType: "network",
              descriptorVersion: "v1",
              signatures: {
                prod: "sig1",
                test: "sig2",
              },
            },
          },
          icons: 123,
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should handle invalid descriptor - null descriptor (line 103)", async () => {
      const invalidData = [
        {
          id: "ethereum",
          descriptors: {
            flex: null,
          },
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });

    it("should handle invalid descriptor - non-object descriptor (line 103)", async () => {
      const invalidData = [
        {
          id: "ethereum",
          descriptors: {
            flex: "should_be_object_not_string",
          },
        },
      ];
      httpMock.get.mockResolvedValue(invalidData);

      const result = await datasource.getDynamicNetworkConfiguration(1);

      expect(result).toEqual(
        Left(
          new Error("Invalid network configuration response for chain ID: 1"),
        ),
      );
    });
  });
});
